import { useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

/** Maps a column key to the value rows are compared on for that column. */
export type SortAccessors<T> = Record<string, (row: T) => unknown>;

const isEmpty = (v: unknown) =>
  v === null || v === undefined || v === '' || Number.isNaN(v);

const toComparable = (v: unknown): number | string => {
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const s = String(v);
  // ISO-ish date strings sort chronologically, not lexically by locale.
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return t;
  }
  return s.toLowerCase();
};

const compareValues = (a: unknown, b: unknown): number => {
  const ca = toComparable(a);
  const cb = toComparable(b);
  if (typeof ca === 'number' && typeof cb === 'number') return ca - cb;
  return String(ca).localeCompare(String(cb), undefined, { numeric: true });
};

/**
 * Client-side column sorting for plain MUI tables. Clicking a column toggles
 * asc → desc → off. Empty values always sink to the bottom regardless of
 * direction, so "Not Set" rows never crowd the top of a sorted list.
 */
export const useTableSort = <T>(
  rows: T[] | undefined,
  accessors: SortAccessors<T>,
  initial?: { key: string; direction: SortDirection }
) => {
  const [sortKey, setSortKey] = useState<string | null>(initial?.key ?? null);
  const [direction, setDirection] = useState<SortDirection>(
    initial?.direction ?? 'asc'
  );

  const requestSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setDirection('asc');
    } else if (direction === 'asc') {
      setDirection('desc');
    } else {
      setSortKey(null);
      setDirection('asc');
    }
  };

  const accessor = sortKey ? accessors[sortKey] : undefined;
  const sortedRows = useMemo(() => {
    const list = rows ?? [];
    if (!accessor) return list;
    const factor = direction === 'asc' ? 1 : -1;
    return list
      .map((row, index) => ({ row, index, value: accessor(row) }))
      .sort((a, b) => {
        const ae = isEmpty(a.value);
        const be = isEmpty(b.value);
        if (ae || be) return ae === be ? a.index - b.index : ae ? 1 : -1;
        return compareValues(a.value, b.value) * factor || a.index - b.index;
      })
      .map(({ row }) => row);
    // accessors are recreated each render; the key identifies the column.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, direction]);

  return { sortedRows, sortKey, direction, requestSort };
};

export type TableSortState = Pick<
  ReturnType<typeof useTableSort>,
  'sortKey' | 'direction' | 'requestSort'
>;
